import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { FlatAssessmentMap, FormSubmission } from '../../types';
import { useLanguage } from '../../i18n/LanguageContext';

const AdultECMAssessmentSection: React.FC = () => {
  const iframeRef = useRef<HTMLIFrameElement | null>(null);
  const { setValue, getValues } = useFormContext<FormSubmission>();
  const { language } = useLanguage();
  const [isReady, setIsReady] = useState(false);

  // Dynamic form source based on language
  const FORM_SRC = language === 'es' ? '/adult_ecm_assessment_form_es.html' : '/adult_ecm_assessment_form.html';

  const isCheckboxInput = (el: Element): el is HTMLInputElement =>
    el.tagName === 'INPUT' && ((el as HTMLInputElement).type || '').toLowerCase() === 'checkbox';

  const collectFromIframe = useCallback(() => {
    const doc = iframeRef.current?.contentWindow?.document;
    if (!doc) return;

    const map: FlatAssessmentMap = {};
    const fields = doc.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
      'input[id], textarea[id], select[id]'
    );

    fields.forEach((el) => {
      if (!el.id) return;
      if (isCheckboxInput(el)) {
        map[el.id] = !!el.checked;
      } else {
        map[el.id] = el.value ?? '';
      }
    });

    setValue('adultEcmAssessment_flat', map, { shouldDirty: true });
  }, [setValue]);

  const clearCheckboxes = useCallback(() => {
    const doc = iframeRef.current?.contentWindow?.document;
    if (!doc) return;

    const checkboxes = doc.querySelectorAll<HTMLInputElement>('input[type="checkbox"][id]');
    checkboxes.forEach((cb) => {
      cb.checked = false;
      cb.removeAttribute('checked');
    });
    collectFromIframe();
  }, [collectFromIframe]);

  const clearAllFields = useCallback(() => {
    const doc = iframeRef.current?.contentWindow?.document;
    if (!doc) return;

    const fields = doc.querySelectorAll<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>(
      'input[id], textarea[id], select[id]'
    );

    fields.forEach((el) => {
      if (isCheckboxInput(el)) {
        el.checked = false;
        el.removeAttribute('checked');
      } else {
        el.value = '';
      }
    });

    collectFromIframe();
  }, [collectFromIframe]);

  const applySavedToIframe = useCallback(() => {
    const doc = iframeRef.current?.contentWindow?.document;
    if (!doc) return;

    const saved = getValues('adultEcmAssessment_flat') || {};
    Object.entries(saved).forEach(([id, value]) => {
      const el = doc.getElementById(id) as HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement | null;
      if (!el) return;
      if (isCheckboxInput(el)) {
        const normalized = typeof value === 'string' ? value.trim().toLowerCase() : value;
        el.checked =
          normalized === true ||
          normalized === 1 ||
          normalized === '1' ||
          normalized === 'true';
      } else {
        el.value = value == null ? '' : String(value);
      }
    });
  }, [getValues]);

  useEffect(() => {
    if (!isReady) return;
    const doc = iframeRef.current?.contentWindow?.document;
    if (!doc) return;

    const handler = () => collectFromIframe();
    doc.addEventListener('input', handler, true);
    doc.addEventListener('change', handler, true);

    return () => {
      doc.removeEventListener('input', handler, true);
      doc.removeEventListener('change', handler, true);
    };
  }, [collectFromIframe, isReady]);

  // Reload iframe when language changes
  useEffect(() => {
    if (iframeRef.current) {
      iframeRef.current.src = FORM_SRC;
      setIsReady(false);
    }
  }, [FORM_SRC, language]);

  const handleLoad = () => {
    setIsReady(true);
    applySavedToIframe();
    collectFromIframe();
  };

  // Translations based on language
  const translations = {
    instruction: language === 'es' 
      ? 'Complete la Evaluación Integral Adulta de ECM aquí. Los valores se sincronizan automáticamente y se usan para generar el PDF de ECM Adulta.'
      : 'Fill the full Adult ECM Assessment here. Values are auto-synced and used to generate the Adult ECM PDF.',
    clearCheckboxes: language === 'es' ? 'Limpiar Casillas' : 'Clear Checkboxes',
    clearAllFields: language === 'es' ? 'Limpiar Todos los Campos' : 'Clear All Fields',
    iframeTitle: language === 'es' ? 'Formulario de Evaluación Integral Adulta de ECM' : 'Adult ECM Assessment Form'
  };

  return (
    <div className="space-y-4">
      <div className="rounded border border-blue-200 bg-blue-50 p-3 text-sm text-blue-900">
        {translations.instruction}
      </div>

      <div className="flex gap-2">
        <button
          type="button"
          onClick={clearCheckboxes}
          className="rounded bg-amber-100 px-3 py-1.5 text-sm font-medium text-amber-900 hover:bg-amber-200"
        >
          {translations.clearCheckboxes}
        </button>
        <button
          type="button"
          onClick={clearAllFields}
          className="rounded bg-red-100 px-3 py-1.5 text-sm font-medium text-red-900 hover:bg-red-200"
        >
          {translations.clearAllFields}
        </button>
      </div>

      <iframe
        ref={iframeRef}
        title={translations.iframeTitle}
        src={FORM_SRC}
        onLoad={handleLoad}
        className="h-[80vh] w-full rounded border border-gray-300 bg-white"
      />
    </div>
  );
};

export default AdultECMAssessmentSection;
